import { useCallback, useEffect, useMemo, useState } from 'react'

import { Layout } from '@loadsmart/loadsmart-ui'

import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { MainContent } from 'core/ui/MainContent'

import { BidState, filterByBidState, findOneByBidState, hasRfpItems } from '../domain/RfpSummary'
import { sortHistoryByDeadline } from '../presenters/rfpHistoryPresenter'
import { EmptyRfpHistoryState } from './EmptyRfpHistoryState'
import { ErrorState } from './ErrorState'
import { useProposalHistory } from './hooks/useProposalHistory'
import { RfpHistoryHeader } from './RfpHistoryHeader'
import { RfpSummaryList } from './RfpSummaryList'

export function RfpHistoryPage() {
  const { rfpHistory, rfpHistoryStatus, fetchRfpHistory } = useProposalHistory()
  const [filter, setFilter] = useState<string>(BidState.Open)

  const computedHistory = useMemo(() => {
    const filteredHistory = filterByBidState(rfpHistory, filter)
    return sortHistoryByDeadline(filteredHistory)
  }, [rfpHistory, filter])

  const onFilterChanged = useCallback((bidState: string) => {
    setFilter(bidState)
  }, [])

  useEffect(() => {
    if (hasRfpItems(rfpHistory)) {
      const hasRfpOpenState = Boolean(findOneByBidState(rfpHistory, BidState.Open))

      if (!hasRfpOpenState) {
        setFilter('all')
      }
    }
  }, [rfpHistory])

  useEffect(() => {
    const abortController = new AbortController()

    fetchRfpHistory(abortController)

    return () => {
      abortController.abort()
    }
  }, [fetchRfpHistory])

  return (
    <MainContent>
      <AsyncContent
        status={rfpHistoryStatus}
        count={rfpHistory?.length}
        loadingState={<LoadingState />}
        errorState={<ErrorState />}
        emptyState={<EmptyRfpHistoryState />}
      >
        <Layout.Stack space="l">
          <RfpHistoryHeader
            rfpCount={rfpHistory.length}
            onFilterChanged={onFilterChanged}
            filter={filter}
          />
          <RfpSummaryList rfpHistory={computedHistory} />
        </Layout.Stack>
      </AsyncContent>
    </MainContent>
  )
}
